import React from "react";
import Button from "../../components/buttons/Button";
import Input from "../../components/inputs/Input";
import Textarea from "../../components/inputs/Textarea";
import ImageUpload from "../../components/uploads/ImageBgUpload";
import Select from "../../components/selects/Select";
import Checkbox from "../../components/checkboxes/Checkbox";
import Text from "../../components/typography/Text";

const ECOSYSTEMS = ["ECAL", "EHL", "EPFL", "HEIG-VD", "UNIL", "OTHER"].map(
  value => ({ value, label: value })
);

export default ({
  data,
  setData,
  errors,
  setError,
  next,
  btnLabel,
  saved,
  loading,
}) => {
  const [selectedEcosystem, setSelectedEcosystem] = React.useState(null);
  const [inUniversity, toggleInUniversity] = React.useReducer(i => !i, false);
  const image =
    data["project_image"] && data["project_image"].base_path
      ? data["project_image"].base_path +
        data["project_image"].breakpoints.xs["1x"]
      : null;

  React.useEffect(() => {
    if (selectedEcosystem && selectedEcosystem.value !== "OTHER")
      setData({ field: "ecosystem", value: selectedEcosystem.value });
    else setData({ field: "ecosystem", value: "" });
  }, [selectedEcosystem]);

  const verifyData = () => {
    setError();
    let isOkay = true;
    if (!data["project_headline"]) {
      setError({
        field: "project_headline",
        message: "Please provide a correct startup headline",
      });
      isOkay = false;
    }
    if (!data["project_short_description"]) {
      setError({
        field: "project_short_description",
        message: "Please provide a correct startup description",
      });
      isOkay = false;
    }
    if (!data["project_description"]) {
      setError({
        field: "project_description",
        message: "Please provide correct startup details",
      });
      isOkay = false;
    }
    if (!data["project_image"]) {
      setError({
        field: "project_image",
        message: "Please provide an image for your startup",
      });
      isOkay = false;
    }
    if (inUniversity && !data["ecosystem"]) {
      setError({
        field: "ecosystem",
        message: "Please provide your university's name",
      });
      isOkay = false;
    }
    if (data["ecosystem"] === "EPFL" && !data["epfl_lab"]) {
      setError({
        field: "epfl_lab",
        message: "Please provide your EPFL Lab",
      });
      isOkay = false;
    }
    if (data["ecosystem"] === "EPFL" && !data["epfl_professor"]) {
      setError({
        field: "epfl_professor",
        message: "Please provide your the name of your professor",
      });
      isOkay = false;
    }
    if (isOkay) next();
  };

  return (
    <div className="oUserForm">
      <h3>Describe what you do</h3>
      <div className="oUserForm_inputs">
        <Input
          label="Startup headline"
          required
          max={80}
          value={data["project_headline"]}
          setValue={value => setData({ field: "project_headline", value })}
          error={errors.some(({ field }) => field === "project_headline")}
        />
        <Textarea
          label="Startup mission"
          required
          max={1250}
          value={data["project_short_description"]}
          setValue={value =>
            setData({ field: "project_short_description", value })
          }
          error={errors.some(
            ({ field }) => field === "project_short_description"
          )}
        />
        <Textarea
          label="More about your project"
          required
          max={3500}
          value={data["project_description"]}
          setValue={value => setData({ field: "project_description", value })}
          error={errors.some(({ field }) => field === "project_description")}
          notice={noticeDescription}
        />
        <ImageUpload
          label="Upload image"
          notice="1300 pixels wide, JPG or PNG"
          setFile={value => setData({ field: "project_image", value })}
          currentImage={image}
          noPreview
        />
      </div>
      <div className="oUserForm_inputs">
        <Checkbox
          checked={inUniversity}
          setValue={toggleInUniversity}
          labelComponent={
            <Text small black>
              I am affiliated with an university
            </Text>
          }
        />
        {inUniversity && (
          <Select
            label="What university?"
            options={ECOSYSTEMS}
            required
            width="100%"
            selected={selectedEcosystem}
            setSelected={value =>
              setSelectedEcosystem(selectedEcosystem === value ? null : value)
            }
            error={errors.some(({ field }) => field === "ecosystem")}
          />
        )}
        {selectedEcosystem?.value === "OTHER" && (
          <Input
            label="What is the name of your university?"
            required
            max={80}
            value={data["ecosystem"]}
            setValue={value => setData({ field: "ecosystem", value })}
            error={errors.some(({ field }) => field === "ecosystem")}
          />
        )}
        {selectedEcosystem?.value === "EPFL" && (
          <>
            <Input
              label="In which EPFL Lab are you hosted?"
              required
              max={80}
              value={data["epfl_lab"]}
              setValue={value => setData({ field: "epfl_lab", value })}
              error={errors.some(({ field }) => field === "epfl_lab")}
            />
            <Input
              label="What is the name of your professor?"
              required
              max={80}
              value={data["epfl_professor"]}
              setValue={value => setData({ field: "epfl_professor", value })}
              error={errors.some(({ field }) => field === "epfl_professor")}
            />
          </>
        )}
      </div>
      <div className="oUserForm_notice">
        This site is protected by reCAPTCHA and the Google
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>{" "}
        and
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{" "}
        apply.
      </div>
      {saved ? (
        <Button big green full>
          Saved !
        </Button>
      ) : (
        <Button onClick={verifyData} big full>
          {loading ? "Registering..." : btnLabel || "Continue"}
        </Button>
      )}
    </div>
  );
};

const noticeDescription = (
  <>
    <div>Think of the following questions...</div>
    <div className="leftPad">
      What’s the main problem you’re trying to solve?
    </div>
    <div className="leftPad">Why is your product different?</div>
    <div className="leftPad">What’s your vision for the product/service?</div>
    <div className="leftPad">Who will use your product/service?</div>
    <div className="leftPad">
      What is the stage of development of your project?
    </div>
  </>
);
